import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReusableTable from "../components/ReusableTable";
import { getApiUrl } from "../utils/api";
import { BASE_URL } from "../utils/api";
import { useNavigate } from "react-router-dom";

const ALL_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "Free Size"];

const Productmanagement = () => {
  const navigate = useNavigate();
  const UserId = "66abc123def4567890abcd12";

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [sizeFilter, setSizeFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [gotoPage, setGotoPage] = useState("");

  const fetchProducts = async (page, searchTerm, size) => {
    const p = page !== undefined ? page : 1;
    const s = searchTerm !== undefined ? searchTerm : search;
    const sz = size !== undefined ? size : sizeFilter;

    try {
      const res = await fetch(getApiUrl("admin-product"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userid: UserId,
          page: p,
          search: s,
          sizeFilter: sz,
        }),
      });

      const result = await res.json();
      if (result.success) {
        setProducts(result.data);
        setFilteredProducts(result.data);
        setCurrentPage(result.pagination.currentPage);
        setTotalPages(result.pagination.totalPages);
        setTotalOrders(result.pagination.totalProducts);
      }
    } catch (error) {
      toast.error("Failed to fetch product list");
    }
  };

  // ✅ Initial load
  useEffect(() => {
    fetchProducts(1, "", "all");
  }, []);

  // ✅ Debounce — search + size filter triggers backend call
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts(1, search, sizeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, sizeFilter]);

  const handleEdit = (product) => {
    navigate("/bsk-secure-admin/new-product", { state: { product } });
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this product?");
    if (!confirm) return;

    const res = await fetch(getApiUrl("product-delete"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productid: id }),
    });

    const result = await res.json();
    if (result.success) {
      toast.success("Product Deleted Successfully!");
      fetchProducts(currentPage, search, sizeFilter);
    } else {
      toast.error("Failed to delete product");
    }
  };

  const handleGotoPage = () => {
    const pageNum = Number(gotoPage);
    if (!pageNum || pageNum < 1 || pageNum > totalPages) {
      toast.error(`Please enter a page between 1 and ${totalPages}`);
      return;
    }
    fetchProducts(pageNum, search, sizeFilter);
  };

  const columns = [
    {
      name: "Si No",
      selector: (row, index) =>
        totalOrders - ((currentPage - 1) * 10 + index),
      width: "70px",
    },
    {
      name: "Product Name",
      cell: (row) => (
        <div
          style={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            lineHeight: 1.4,
            maxHeight: "80px",
            overflow: "hidden",
          }}
          title={row.productName}
        >
          {row.productName}
        </div>
      ),
    },
    {
      name: "Price",
      selector: (row) => `₹ ${row.price}`,
      width: "100px",
    },
    {
      name: "Discount",
      selector: (row) => `${row.discount}%`,
      width: "90px",
    },
    {
      name: "Remaining",
      selector: (row) => row.totalProducts,
      width: "100px",
    },
    {
      name: "Available Sizes",
      cell: (row) => {
        const available = Object.entries(row.sizes || {})
          .filter(([, qty]) => qty > 0)
          .map(([size, qty]) => (
            <span
              key={size}
              style={{
                display: "inline-block",
                background:
                  sizeFilter === size ? "#4338ca" : "#eef2ff",
                color: sizeFilter === size ? "#fff" : "#4338ca",
                borderRadius: "4px",
                padding: "1px 6px",
                fontSize: "11px",
                fontWeight: 600,
                margin: "1px",
              }}
            >
              {size}: {qty}
            </span>
          ));
        return available.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2px", padding: "4px 0" }}>
            {available}
          </div>
        ) : (
          <span className="badge bg-danger">Out of Stock</span>
        );
      },
      width: "200px",
    },
    {
      name: "Image",
      cell: (row) => (
        <img
          src={`${BASE_URL}${row.mainImage}`}
          alt={row.productName}
          width="60"
          height="60"
          style={{ borderRadius: "8px", objectFit: "cover" }}
        />
      ),
      width: "90px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-warning btn-sm"
            onClick={() => handleEdit(row)}
          >
            <i className="fa-regular fa-pen-to-square"></i>
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDelete(row._id)}
          >
            <i className="fa-regular fa-trash-can"></i>
          </button>
        </div>
      ),
      width: "100px",
    },
  ];

  return (
    <div className="container-fluid mt-4">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Back */}
      <div className="mb-2">
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "4px 10px",
            fontSize: "12px",
            borderRadius: "6px",
            border: "1px solid #ddd",
            background: "#f5f5f5",
            cursor: "pointer",
          }}
        >
          ← Back
        </button>
      </div>

      {/* Header + Search + Size Filter + Add */}
      <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
        <h3 className="fw-bold">Product Management</h3>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          {/* Search */}
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: "220px" }}
            placeholder="🔍 Search product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* ✅ Size Filter Dropdown */}
          <select
            className="form-select"
            style={{ maxWidth: "160px" }}
            value={sizeFilter}
            onChange={(e) => setSizeFilter(e.target.value)}
          >
            <option value="all">All Sizes</option>
            {ALL_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>

          {/* Clear filter button */}
          {(search || sizeFilter !== "all") && (
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setSearch("");
                setSizeFilter("all");
              }}
            >
              Clear
            </button>
          )}

          <button
            className="btn btn-primary"
            onClick={() => navigate("/bsk-secure-admin/new-product")}
          >
            Add Product
          </button>
        </div>
      </div>

      {/* Active filter badge */}
      {sizeFilter !== "all" && (
        <div className="mb-2">
          <span
            style={{
              background: "#eef2ff",
              color: "#4338ca",
              padding: "4px 10px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            Showing size: {sizeFilter} — {totalOrders} product(s) found
          </span>
        </div>
      )}

      {/* Go to page */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="text-muted small">Go to page:</span>
        <input
          type="number"
          className="form-control"
          style={{ width: "80px" }}
          value={gotoPage}
          onChange={(e) => setGotoPage(e.target.value)}
          min={1}
          max={totalPages}
        />
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleGotoPage}
        >
          Go
        </button>
        <span className="text-muted small">
          (Total: <strong>{totalOrders}</strong> products)
        </span>
      </div>

      {/* Table */}
      <ReusableTable
        columns={columns}
        data={filteredProducts}
        title="Product List"
        currentPage={currentPage}
        totalPages={totalPages}
        totalOrders={totalOrders}
        onChangePage={(page) => fetchProducts(page, search, sizeFilter)}
      />
    </div>
  );
};

export default Productmanagement;